import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { Tabs, Tab, Box } from '@mui/material';
import FinancialAdvisor from '../graph/FinancialAdvisor';
import WasteResourcesList from '../graph/WasteResourcesList';
import CostIntelligenceTab from './finops/CostIntelligenceTab';
import WasteManagementTab from './finops/WasteManagementTab';
import { executeCURQuery } from '../../services/finopsApi';

const FinOpsAccountView = ({ dashboardData, accountId, cloudAccounts, onBack }) => {
  const account = cloudAccounts.find(a => a.awsAccountId === accountId);
  const [spCoverage, setSpCoverage] = useState(null);
  const [topResources, setTopResources] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [accountDiscounts, setAccountDiscounts] = useState(null);
  const [accountTax, setAccountTax] = useState(0);
  const [aggregates, setAggregates] = useState([]);
  const [chartType, setChartType] = useState('bar');

  useEffect(() => {
    loadAccountCURData();
    loadAccountDiscounts();
    loadAccountAggregates();
    
    // Initialize Bootstrap tooltips
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => {
      if (window.bootstrap && window.bootstrap.Tooltip) {
        return new window.bootstrap.Tooltip(tooltipTriggerEl);
      }
      return null;
    });
    
    return () => {
      tooltipList.forEach(tooltip => tooltip?.dispose());
    };
  }, [accountId]);

  const loadAccountCURData = async () => {
    try {
      const tenantId = localStorage.getItem('organizationID');
      if (!tenantId) return;

      const [coverage, resources] = await Promise.all([
        executeCURQuery(tenantId, 'sp_coverage', { account_id: accountId }).catch(() => null),
        executeCURQuery(tenantId, 'resource_details', { account_id: accountId, limit: 10 }).catch(() => ({ resources: [] }))
      ]);

      // Aggregate SP coverage across all services for this account
      if (coverage?.coverage_by_service) {
        const services = coverage.coverage_by_service;
        const totalCovered = services.reduce((sum, s) => sum + (s.sp_covered || 0), 0);
        const totalOnDemand = services.reduce((sum, s) => sum + (s.on_demand || 0), 0);
        const totalSavings = services.reduce((sum, s) => sum + (s.savings_realized || 0), 0);
        const totalCost = totalCovered + totalOnDemand;
        const coveragePct = totalCost > 0 ? (totalCovered / totalCost * 100) : 0;
        
        setSpCoverage({
          coverage_pct: coveragePct,
          savings_realized: totalSavings,
          sp_covered: totalCovered,
          on_demand: totalOnDemand
        });
      }
      setTopResources(resources?.resources || []);
    } catch (error) {
      console.error('Error loading account CUR data:', error);
    }
  };
  
  const loadAccountDiscounts = async () => {
    try {
      const tenantID = localStorage.getItem('tenantID');
      if (!tenantID) return;
      
      const currentMonth = new Date().toISOString().substring(0, 7);
      const response = await fetch(`https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/finops/aggregates?tenantID=${tenantID}&month=${currentMonth}`);
      if (!response.ok) return;
      
      const data = await response.json();
      const accountData = data.accounts?.find(acc => acc.accountId === accountId);
      if (accountData?.discounts && accountData.discounts.total < 0) {
        setAccountDiscounts(accountData.discounts);
      }
      if (accountData?.tax && accountData.tax > 0) {
        setAccountTax(accountData.tax);
      }
    } catch (error) {
      console.error('Error loading account discounts:', error);
    }
  };
  
  const loadAccountAggregates = async () => {
    try {
      const tenantID = localStorage.getItem('tenantID');
      if (!tenantID) return;
      
      const response = await fetch(`https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/finops/aggregates?tenantID=${tenantID}&accountId=${accountId}&months=3`);
      if (!response.ok) return;
      
      const data = await response.json();
      setAggregates(data.months || []);
    } catch (error) {
      console.error('Error loading account aggregates:', error);
    }
  };
  
  const trendChartOptions = {
    chart: { type: 'area', toolbar: { show: false }, sparkline: { enabled: false } },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 } },
    xaxis: { 
      categories: dashboardData?.cost?.trend?.map(t => t.date) || [],
      labels: { style: { colors: '#6c757d', fontSize: '11px' } }
    },
    yaxis: { 
      labels: { 
        formatter: (val) => `$${val?.toFixed(0)}`,
        style: { colors: '#6c757d', fontSize: '11px' }
      }
    },
    colors: ['#6D5BFF'],
    grid: { borderColor: '#e9ecef' },
    tooltip: { y: { formatter: (val) => `$${val?.toFixed(2)}` } }
  };

  const trendChartSeries = [{
    name: 'Daily Cost',
    data: dashboardData?.cost?.trend?.map(t => t.cost) || []
  }];

  const budget = dashboardData?.optimization?.budgets?.details?.[0];
  const budgetUsage = budget ? (parseFloat(budget.actual) / parseFloat(budget.limit) * 100) : 0;

  return (
    <>
      <button 
        className="btn btn-outline-secondary mb-3" 
        onClick={onBack}
      >
        ← Back to Organization View
      </button>

      <div className="alert alert-info mb-4">
        <strong>Account:</strong> {account?.alias || accountId} ({accountId})
      </div>

      <div className="row g-4">
        <div className="col-md-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h6 className="text-muted mb-2">Monthly Spend</h6>
                  <h3 className="mb-1" style={{ fontWeight: '700', color: '#342ead' }}>
                    ${dashboardData?.cost?.total?.toFixed(2) || '0.00'}
                  </h3>
                  <p className="text-muted small mb-0">Net: ${dashboardData?.cost?.net?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="col-md-6 text-end">
                  {accountDiscounts && accountDiscounts.total < 0 && (
                    <>
                      <h6 className="text-muted mb-2">Total Savings</h6>
                      <h3 className="mb-1 text-success" style={{ fontWeight: '700' }}>
                        ${accountDiscounts.total.toFixed(2)}
                      </h3>
                      <p className="text-muted small mb-0">
                        <small>
                          {Math.abs(accountDiscounts.edp) > 0.01 && `EDP: $${Math.abs(accountDiscounts.edp).toFixed(2)}`}
                          {Math.abs(accountDiscounts.edp) > 0.01 && Math.abs(accountDiscounts.savingsPlan) > 0.01 && ' | '}
                          {Math.abs(accountDiscounts.savingsPlan) > 0.01 && `SP: $${Math.abs(accountDiscounts.savingsPlan).toFixed(2)}`}
                        </small>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <h6 className="text-muted mb-2">Budget Status</h6>
              <h3 className="mb-1" style={{ fontWeight: '700', color: budgetUsage > 90 ? '#dc3545' : budgetUsage > 75 ? '#ffc107' : '#28a745' }}>
                {budget ? `${budgetUsage.toFixed(0)}%` : 'N/A'}
              </h3>
              <p className="text-muted small mb-0">{budget ? `$${parseFloat(budget.actual).toFixed(0)} of $${parseFloat(budget.limit).toFixed(0)}` : 'No budget set'}</p>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <h6 className="text-muted mb-2">Waste Resources</h6>
              <h3 className="mb-1" style={{ fontWeight: '700', color: '#ffc107' }}>
                {parseInt(dashboardData?.waste?.summary?.totalWasteResources || 0)}
              </h3>
              <p className="text-muted small mb-0">
                {parseInt(dashboardData?.waste?.summary?.criticalCount || 0)} Critical
              </p>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <h6 className="text-muted mb-0 me-2">SP Coverage</h6>
                <span 
                  className="badge bg-light text-dark" 
                  style={{ cursor: 'help' }}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-html="true"
                  data-bs-title="<strong>Account SP Coverage:</strong><br/>• {spCoverage?.coverage_pct?.toFixed(1) || '0'}% of this account's usage is discounted<br/>• Savings: ${spCoverage?.savings_realized?.toFixed(2) || '0'}/month<br/><br/><strong>To increase:</strong><br/>• Buy Savings Plans in AWS Console<br/>• Target 40-50% coverage for optimal savings"
                >
                  ℹ️
                </span>
              </div>
              <h3 className="mb-1" style={{ fontWeight: '700', color: spCoverage?.coverage_pct > 30 ? '#28a745' : '#ffc107' }}>
                {spCoverage?.coverage_pct?.toFixed(1) || '0'}%
              </h3>
              <p className="text-muted small mb-0">
                ${spCoverage?.savings_realized?.toFixed(2) || '0.00'} saved
              </p>
            </div>
          </div>
        </div>

        <div className="col-md-8">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">Daily Cost Trend (Current Month)</h5>
              <Chart options={trendChartOptions} series={trendChartSeries} type="area" height={280} />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="h-100" style={{ maxHeight: '380px', overflowY: 'auto' }}>
            <FinancialAdvisor insights={dashboardData?.insights} isLoading={false} />
          </div>
        </div>

        {aggregates.length > 0 && (
          <>
            <div className="col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h5 className="card-title mb-3">Monthly Cost Trend (Last 3 Months)</h5>
                  <Chart 
                    options={{
                      chart: { type: 'line', toolbar: { show: false } },
                      stroke: { curve: 'smooth', width: 3 },
                      markers: { size: 5 },
                      xaxis: { 
                        categories: aggregates.slice().reverse().map(m => {
                          const [year, month] = m.month.split('-');
                          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          return `${monthNames[parseInt(month)-1]} ${year}`;
                        }),
                        labels: { style: { fontSize: '12px', fontWeight: 600 } }
                      },
                      yaxis: { 
                        labels: { 
                          formatter: (val) => `$${(val/1000).toFixed(1)}K`,
                          style: { fontSize: '12px' }
                        }
                      },
                      colors: ['#342ead'],
                      tooltip: { y: { formatter: (val) => `$${val.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` } },
                      dataLabels: {
                        enabled: true,
                        formatter: (val) => `$${(val/1000).toFixed(1)}K`,
                        style: { fontSize: '11px', colors: ['#342ead'], fontWeight: 'bold' },
                        offsetY: -10
                      }
                    }}
                    series={[{
                      name: 'Monthly Cost',
                      data: aggregates.slice().reverse().map(m => parseFloat(m.netCost))
                    }]}
                    type="line"
                    height={300}
                  />
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h5 className="card-title mb-3">
                    Top Services Trend (Last 3 Months)
                    <div className="btn-group btn-group-sm float-end" role="group">
                      <button 
                        type="button" 
                        className={`btn ${chartType === 'bar' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setChartType('bar')}
                        style={{ fontSize: '11px', padding: '4px 12px' }}
                      >
                        Bar
                      </button>
                      <button 
                        type="button" 
                        className={`btn ${chartType === 'area' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setChartType('area')}
                        style={{ fontSize: '11px', padding: '4px 12px' }}
                      >
                        Area
                      </button>
                    </div>
                  </h5>
                  <Chart 
                    options={{
                      chart: { 
                        type: chartType, 
                        stacked: true,
                        toolbar: { show: false }
                      },
                      plotOptions: chartType === 'bar' ? { 
                        bar: { horizontal: false, columnWidth: '60%' } 
                      } : {},
                      dataLabels: { enabled: false },
                      stroke: chartType === 'area' ? { curve: 'smooth', width: 2 } : { width: 0 },
                      fill: {
                        type: chartType === 'area' ? 'gradient' : 'solid',
                        gradient: chartType === 'area' ? { opacityFrom: 0.6, opacityTo: 0.2 } : {}
                      },
                      xaxis: { 
                        categories: aggregates.slice().reverse().map(m => {
                          const [year, month] = m.month.split('-');
                          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          return `${monthNames[parseInt(month)-1]} ${year}`;
                        }),
                        labels: { style: { fontSize: '12px' } }
                      },
                      yaxis: { 
                        labels: { 
                          formatter: (val) => `$${(val/1000).toFixed(1)}K`,
                          style: { fontSize: '12px' }
                        }
                      },
                      legend: { 
                        position: 'bottom',
                        fontSize: '11px',
                        markers: { width: 10, height: 10 }
                      },
                      tooltip: { 
                        shared: true,
                        intersect: false,
                        y: { formatter: (val) => `$${val.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` }
                      },
                      colors: ['#342ead', '#6D5BFF', '#9D8DFF', '#00E396', '#FEB019']
                    }}
                    series={(() => {
                      const reversedAggregates = aggregates.slice().reverse();
                      const allServices = new Set();
                      reversedAggregates.forEach(m => m.services?.slice(0, 5).forEach(s => allServices.add(s.service)));
                      return Array.from(allServices).slice(0, 5).map(serviceName => ({
                        name: serviceName.length > 35 ? serviceName.substring(0, 35) + '...' : serviceName,
                        data: reversedAggregates.map(m => {
                          const svc = m.services?.find(s => s.service === serviceName);
                          return svc ? parseFloat(svc.cost) : 0;
                        })
                      }));
                    })()}
                    type={chartType}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Tabs Section */}
        <div className="col-md-12">
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Overview" />
              <Tab label="Cost Intelligence" />
              <Tab label="Waste Management" />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <>
              <WasteResourcesList wasteData={dashboardData?.waste} accountFilter={accountId} />

        {topResources.length > 0 && (
          <div className="col-md-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-3">Top 10 Resources by Cost</h5>
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Resource ID</th>
                        <th>Service</th>
                        <th>Type</th>
                        <th>Region</th>
                        <th className="text-end">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topResources.map((resource, idx) => (
                        <tr key={idx}>
                          <td>
                            <small className="font-monospace">
                              {resource.resource_id.length > 40 
                                ? resource.resource_id.substring(0, 40) + '...' 
                                : resource.resource_id}
                            </small>
                          </td>
                          <td>{resource.service}</td>
                          <td><small>{resource.instance_type}</small></td>
                          <td><small>{resource.region}</small></td>
                          <td className="text-end" style={{ fontWeight: '500' }}>
                            ${resource.cost?.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="col-md-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h5 className="card-title mb-3">Service Cost Breakdown</h5>
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Service</th>
                      <th className="text-end" style={{ width: '120px', paddingRight: '15px' }}>Cost</th>
                      <th className="text-end" style={{ width: '80px', paddingRight: '15px' }}>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.cost?.byService?.slice(0, 10).map((service, idx) => {
                      const servicesSum = dashboardData?.cost?.byService?.reduce((sum, s) => sum + parseFloat(s.cost), 0) || 1;
                      const percentage = (parseFloat(service.cost) / servicesSum * 100).toFixed(1);
                      return (
                        <tr key={idx}>
                          <td>{service.service}</td>
                          <td className="text-end" style={{ fontWeight: '500' }}>
                            ${parseFloat(service.cost).toFixed(2)}
                          </td>
                          <td className="text-end">
                            <span className="badge" style={{ 
                              backgroundColor: percentage > 20 ? '#dc3545' : percentage > 10 ? '#ffc107' : '#e9ecef',
                              color: percentage > 10 ? '#fff' : '#6c757d'
                            }}>
                              {percentage}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td><strong>Gross Cost</strong></td>
                      <td className="text-end" colSpan="2">
                        <strong>${(() => {
                          const servicesSum = dashboardData?.cost?.byService?.reduce((sum, s) => sum + parseFloat(s.cost), 0) || 0;
                          return servicesSum.toFixed(2);
                        })()}</strong>
                      </td>
                    </tr>
                    {(() => {
                      const servicesSum = dashboardData?.cost?.byService?.reduce((sum, s) => sum + parseFloat(s.cost), 0) || 0;
                      const discount = servicesSum - (dashboardData?.cost?.total || 0);
                      return discount > 1 ? (
                        <tr>
                          <td><strong>Total Savings</strong></td>
                          <td className="text-end text-success" colSpan="2">
                            <strong>-${discount.toFixed(2)}</strong>
                          </td>
                        </tr>
                      ) : null;
                    })()}
                    <tr style={{ borderTop: '2px solid #dee2e6' }}>
                      <td><strong>Net Cost</strong></td>
                      <td className="text-end" colSpan="2">
                        <strong style={{ color: '#342ead', fontSize: '1.1rem' }}>
                          ${dashboardData?.cost?.total?.toFixed(2) || '0.00'}
                        </strong>
                      </td>
                    </tr>
                    {accountTax > 0 && (
                      <tr>
                        <td><em className="text-muted">  (includes Tax)</em></td>
                        <td className="text-end text-muted" colSpan="2">
                          <em>${accountTax.toFixed(2)} ({((accountTax / (dashboardData?.cost?.total || 1)) * 100).toFixed(1)}%)</em>
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
            </>
          )}

          {activeTab === 1 && (
            <CostIntelligenceTab costIntelligence={dashboardData?.costIntelligence} />
          )}

          {activeTab === 2 && (
            <WasteManagementTab waste={dashboardData?.waste} />
          )}
        </div>
      </div>
    </>
  );
};

export default FinOpsAccountView;
